/**
 * CypAsdSSISideNavbar — Left sidebar for the CYP ASD SSI module.
 *
 * Supports two modes controlled via the display-preferences panel:
 *   • "compact" (default) — Badge-only vertical strip. The badge itself
 *     turns green when both evidence ratings are recorded, giving an
 *     at-a-glance progress indicator without any extra icons or dots.
 *   • "expanded" — Full-width sidebar with badge, short title, and a
 *     green checkmark for completed criteria.
 *
 * Functional Impairment is hidden from the sidebar for the time being.
 */

import React from 'react';
import { CheckIcon, LockClosedIcon } from '@heroicons/react/solid';

export default function CypAsdSSISideNavbar({
  criteria,
  activeItemId,
  onSelectItem,
  criterionData,
  sidebarMode = 'compact',
}) {
  const isCompact = sidebarMode === 'compact';

  const isCriterionComplete = (criterionId) => {
    const data = criterionData?.[criterionId];
    if (!data) return false;
    return !!(data.evidence_early && data.evidence_current);
  };

  const isCriterionStarted = (criterionId) => {
    const data = criterionData?.[criterionId];
    if (!data) return false;
    return !!(data.evidence_early || data.evidence_current);
  };

  /**
   * Badge colour logic:
   *   • Complete → green (emerald) regardless of active state
   *   • Active (not complete) → brand primary
   *   • Started → light primary tint
   *   • Default → neutral grey
   *
   * Selection is indicated by a ring/outline around the badge rather
   * than overriding the completion colour — so green stays green.
   */
  const badgeClasses = (isActive, isComplete, isStarted) => {
    if (isComplete) return 'bg-emerald-500 text-white';
    if (isActive) return 'bg-primary text-white';
    if (isStarted) return 'bg-primary/10 text-primary';
    return 'bg-gray-200 text-gray-500';
  };

  const renderItem = (item) => {
    const isActive = activeItemId === item.id;
    const isComplete = isCriterionComplete(item.id);
    const isStarted = isCriterionStarted(item.id);
    const isAvailable = item.available;

    if (isCompact) {
      return (
        <div
          key={item.id}
          onClick={() => isAvailable && onSelectItem(item.id)}
          className={`
            rounded-lg flex items-center justify-center transition-all duration-200
            ${!isAvailable ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            ${isActive ? 'scale-110 shadow-sm' : 'hover:scale-105'}
          `}
          role="button"
          tabIndex={isAvailable ? 0 : -1}
          aria-label={`${item.fullTitle}${isComplete ? ' — complete' : isStarted ? ' — started' : ''}`}
          aria-disabled={!isAvailable}
          title={item.shortTitle}
        >
          <div className={`
            w-9 h-7 rounded-md flex items-center justify-center text-xs font-bold
            transition-all duration-200
            ${badgeClasses(isActive, isComplete, isStarted)}
            ${isActive ? 'ring-2 ring-primary ring-offset-1' : ''}
          `}>
            {isAvailable ? item.id : (
              <LockClosedIcon className="w-3 h-3" />
            )}
          </div>
        </div>
      );
    }

    /* ---- Expanded mode ---- */
    return (
      <div
        key={item.id}
        onClick={() => isAvailable && onSelectItem(item.id)}
        className={`
          self-stretch px-3 py-2 rounded-lg text-left
          flex items-center gap-2 transition-all duration-200 border-2
          ${!isAvailable ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          ${isActive && isComplete ? 'bg-emerald-50 border-primary shadow-sm' : ''}
          ${isActive && !isComplete ? 'bg-white border-primary shadow-sm' : ''}
          ${!isActive && isComplete ? 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100' : ''}
          ${!isActive && !isComplete && isAvailable ? 'border-transparent hover:bg-gray-100' : ''}
          ${!isActive && !isAvailable ? 'border-transparent' : ''}
        `}
        role="button"
        tabIndex={isAvailable ? 0 : -1}
        aria-label={`${item.fullTitle}${isComplete ? ' — complete' : isStarted ? ' — started' : ''}`}
        aria-disabled={!isAvailable}
      >
        <div className={`
          min-w-[28px] h-6 px-1.5 rounded flex items-center justify-center text-xs font-bold flex-shrink-0
          ${badgeClasses(isActive, isComplete, isStarted)}
        `}>
          {item.id}
        </div>

        <div className={`
          flex-1 text-sm leading-tight text-left truncate
          ${isActive ? 'font-bold text-gray-900' : 'font-medium text-gray-600'}
        `}>
          {item.shortTitle}
        </div>

        <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
          {isAvailable ? (
            isComplete ? (
              <CheckIcon className="w-4 h-4 text-emerald-500 animate-checkmark-pop" />
            ) : null
          ) : (
            <LockClosedIcon className="w-3.5 h-3.5 text-gray-300" />
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      className={`
        mt-6 border-r border-gray-200
        flex flex-col items-stretch sticky top-8
        transition-all duration-300 ease-in-out
        ${isCompact ? 'px-3 min-w-[62px] max-w-[62px]' : 'px-4 min-w-[260px] max-w-[300px]'}
      `}
    >
      {/* Header — only shown in expanded mode */}
      {!isCompact && (
        <div className="text-left mb-2">
          <h3 className="text-gray-900 text-base font-bold leading-tight mb-0.5">
            Criteria
          </h3>
          <p className="text-gray-500 text-xs leading-tight">
            DSM-5 ASD
          </p>
        </div>
      )}

      {/* Criterion items */}
      <div className={`flex flex-col ${isCompact ? 'gap-2 items-center' : 'gap-0.5'}`}>
        {criteria.map(renderItem)}
      </div>

      {/*
        Functional Impairment is hidden from the sidebar for the time being.
        The full FI panel is still reachable via the "Go to Functional
        Impairment" link at the bottom of each criterion page.
      */}

      {/* Divider + Review link — mirrors the Observation sidebar's Summary button.
          Calls onSelectItem('REVIEW') which the parent intercepts to validate
          evidence decisions before navigating to the Review page. */}
      {(() => {
        const isReviewActive = activeItemId === 'REVIEW';
        return (
          <div className={`border-t border-gray-200 ${isCompact ? 'w-8 mx-auto mt-3 pt-3' : 'mt-2 pt-2'}`}>
            {isCompact ? (
              <div
                onClick={() => onSelectItem('REVIEW')}
                className={`rounded-lg flex items-center justify-center cursor-pointer transition-all duration-200 ${isReviewActive ? 'scale-110 shadow-sm' : 'hover:scale-105'}`}
                role="button"
                tabIndex={0}
                title="Go to Review"
              >
                <div className={`w-9 h-7 rounded-md flex items-center justify-center transition-all duration-200 ${
                  isReviewActive
                    ? 'bg-primary text-white ring-2 ring-primary ring-offset-1'
                    : 'bg-gray-200 text-gray-500 hover:bg-primary hover:text-white'
                }`}>
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </div>
            ) : (
              <div
                onClick={() => onSelectItem('REVIEW')}
                className={`
                  self-stretch px-3 py-2 rounded-lg text-left
                  flex items-center gap-2 transition-all duration-200 border-2
                  cursor-pointer
                  ${isReviewActive
                    ? 'bg-primary/10 border-primary/20'
                    : 'border-transparent hover:bg-gray-100'}
                `}
                role="button"
                tabIndex={0}
              >
                <div className={`min-w-[28px] h-6 px-1.5 rounded flex items-center justify-center transition-all duration-200 ${
                  isReviewActive
                    ? 'bg-primary text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}>
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className={`flex-1 text-sm leading-tight text-left font-medium ${
                  isReviewActive ? 'text-gray-900 font-semibold' : 'text-gray-600'
                }`}>
                  Review
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Keyframe animation for the checkmark pop-in (expanded mode) */}
      <style>{`
        @keyframes checkmark-pop {
          0% {
            opacity: 0;
            transform: scale(0.3);
          }
          50% {
            opacity: 1;
            transform: scale(1.2);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-checkmark-pop {
          animation: checkmark-pop 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
      `}</style>
    </div>
  );
}
